import React from "react";

/**
 * Injecte les CSS custom properties du thème 2026 sur la racine .theme-2026.
 * Lit `config.theme2026.tokens` (objet plat). Les défauts viennent du thème lui-même
 * (palette parchment + navy + brass, typo serif Fraunces optionnelle).
 *
 * Tous les composants 2026 consomment ces variables via `var(--xxx, fallback)` —
 * pas de hardcode de couleur dans les composants.
 */
const DEFAULTS = {
    primary:           "#1B2A4E",
    primaryContrast:   "#FFFFFF",
    secondary:         "#B98A3B",
    secondaryContrast: "#FFFFFF",
    bg:                "#F6F1E6",
    bg2:               "#EDE5D0",
    surface:           "#FFFCF5",
    ink:               "#14181F",
    ink2:              "#36404F",
    ink3:              "#6B7382",
    line:              "#E2D9C2",
    line2:             "#CDC1A2",
    fontDisplay:       "Fraunces, ui-serif, Georgia, serif",
    fontBody:          "Inter, system-ui, -apple-system, sans-serif",
    fontHe:            "Frank Ruhl Libre, system-ui, serif",
};

export default function ThemeVars({ config }) {
    const tokens = { ...DEFAULTS, ...(config?.theme2026?.tokens || {}) };

    // Override NProgress avec la primary du thème 2026.
    // NProgress est inséré dans <body> hors .theme-2026, donc on cible
    // directement #nprogress avec !important.
    const nprogressCss = `
#nprogress .bar { background: ${tokens.primary} !important; }
#nprogress .peg { box-shadow: 0 0 12px ${tokens.primary}, 0 0 6px ${tokens.primary} !important; }
`;

    const css = `
${nprogressCss}
.theme-2026 {
  --primary: ${tokens.primary};
  --primary-contrast: ${tokens.primaryContrast};
  --secondary: ${tokens.secondary};
  --secondary-contrast: ${tokens.secondaryContrast};
  --bg: ${tokens.bg};
  --bg-2: ${tokens.bg2};
  --surface: ${tokens.surface};
  --ink: ${tokens.ink};
  --ink-2: ${tokens.ink2};
  --ink-3: ${tokens.ink3};
  --line: ${tokens.line};
  --line-2: ${tokens.line2};

  --font-display: ${tokens.fontDisplay};
  --font-body: ${tokens.fontBody};
  --font-he: ${tokens.fontHe};

  --r-sm: 8px;
  --r-md: 14px;
  --r-lg: 20px;
  --r-xl: 28px;
  --r-pill: 999px;

  --shadow-sm: 0 1px 2px rgba(20,24,31,.05);
  --shadow-md: 0 8px 22px -14px rgba(20,24,31,.22), 0 2px 4px rgba(20,24,31,.06);
  --shadow-lg: 0 30px 60px -28px rgba(20,24,31,.32), 0 6px 16px -10px rgba(20,24,31,.18);

  --container: 1200px;
}

/* Container utilitaire */
.theme-2026 .t26-container {
  width: 100%;
  max-width: var(--container);
  margin-inline: auto;
  padding-inline: clamp(16px, 4vw, 32px);
}

/* Fond global */
.theme-2026 {
  background:
    radial-gradient(1200px 600px at 90% -200px, color-mix(in oklab, var(--secondary) 14%, transparent) 0%, transparent 60%),
    radial-gradient(900px 500px at -10% 100%, color-mix(in oklab, var(--primary) 10%, transparent) 0%, transparent 70%),
    var(--bg);
  color: var(--ink);
  font-family: var(--font-body);
  min-height: 100vh;
}

/* En embed, le wrapper ne doit imposer AUCUNE hauteur : c'est le parent qui
   dimensionne l'iframe d'après la hauteur qu'on lui poste. Un "100vh" ici
   devient, une fois figé en pixels, un plancher de 560 à 1000px selon l'écran
   du visiteur — un formulaire de 250px se retrouvait suivi d'un grand vide, et
   la même page n'avait pas la même hauteur d'un visiteur à l'autre. */
.theme-2026--embed {
  min-height: 0;
}

/* Reset minimal body/html : retire la marge par défaut 8px du navigateur qui
   crée un cadre blanc autour du thème (ex-rôle de travel.css/bootstrap retiré).
   On scope au moins via :has(.theme-2026) pour ne pas affecter les pages
   d'autres thèmes si Next les sert dans le même body. */
html:has(.theme-2026),
html:has(.theme-2026) body {
  margin: 0;
  padding: 0;
  background: var(--bg);
}

[dir="rtl"] .theme-2026 {
  font-family: var(--font-he);
}

/* IMPORTANT — pas de sélecteur global sur balises HTML (a, button, h1-h6) sous
   .theme-2026 : ça casserait le contenu HTML libre (WebEditor, AiContent,
   pages éditeur legacy, contenu personnalisé, etc.).
   Chaque composant 2026 stylise UNIQUEMENT ses propres éléments via les
   classes .t26-* qu'il dépose lui-même. */

/* Couleur de tous les titres/textes hero/banner forcée à blanc (image sombre).
   Le ciblage se fait sur les classes .t26-* précises, pas sur la balise <h1>. */
.theme-2026 .t26-hero__content,
.theme-2026 .t26-hero__title,
.theme-2026 .t26-hero__sub,
.theme-2026 .t26-hero__desc,
.theme-2026 .t26-hero__eyebrow,
.theme-2026 .t26-banner__content,
.theme-2026 .t26-banner__title,
.theme-2026 .t26-banner__subtitle,
.theme-2026 .t26-banner__desc,
.theme-2026 .t26-banner__eyebrow {
  color: #fff;
}
.theme-2026 .t26-hero__title em {
  display: block;
  color: color-mix(in oklab, white 65%, var(--secondary) 35%);
}
.theme-2026 .t26-hero__sub  { color: rgba(255,255,255,.86); }
.theme-2026 .t26-hero__desc { color: rgba(255,255,255,.74); }
.theme-2026 .t26-banner__subtitle { color: rgba(255,255,255,.92); }
.theme-2026 .t26-banner__desc     { color: rgba(255,255,255,.82); }

/* Force le texte blanc sur TOUS les boutons primary/secondary du thème,
   quelle que soit la couleur primary/secondary configurée par l'admin.
   Évite "texte noir sur fond brass/cyan clair" partout (hero, banner, cards,
   header CTAs, donation strip, footer, sections preview…). */
.theme-2026 .tr-btn--primary,
.theme-2026 .tr-btn--primary *,
.theme-2026 .tr-btn--secondary,
.theme-2026 .tr-btn--secondary * {
  color: #fff;
}
/* Boutons ghost dans hero/banner : sur image sombre, texte blanc + bordure blanche */
.theme-2026 .t26-hero__ctas .tr-btn--ghost,
.theme-2026 .t26-banner__ctas .tr-btn--ghost {
  color: #fff;
  background: rgba(255,255,255,.10);
  border-color: rgba(255,255,255,.35);
  backdrop-filter: blur(8px);
}
.theme-2026 .t26-hero__ctas .tr-btn--ghost:hover,
.theme-2026 .t26-banner__ctas .tr-btn--ghost:hover {
  background: rgba(255,255,255,.22);
  border-color: #fff;
}
/* Idem pour le bouton CTA des caisses (utilise sa propre classe t26-caisse__cta) */
.theme-2026 .t26-caisse__cta,
.theme-2026 .t26-caisse__cta * {
  color: #fff;
}
/* Idem pour le bandeau de don (gradient primary→secondary, texte toujours blanc).
   Le bouton CTA (.t26-donate-strip__btn) a sa propre couleur ink forcée en !important. */
.theme-2026 .t26-donate-strip,
.theme-2026 .t26-donate-strip * {
  color: #fff;
}

/* Helper pour flipper les icônes directionnelles en RTL */
[dir="rtl"] .theme-2026 .t26-flip-rtl { transform: scaleX(-1); }

/* Travel CSS force "display: block" sur tous les SVG sous .travel-page,
   ce qui casse l'alignement inline dans nos pills (eyebrow, badges, boutons).
   On remet inline-block dans les containers inline-flex. */
.theme-2026 .t26-hero__eyebrow svg,
.theme-2026 .t26-banner__eyebrow svg,
.theme-2026 .t26-section__eyebrow svg,
.theme-2026 .tr-btn svg,
.theme-2026 .t26-caisse__cta svg,
.theme-2026 .t26-donate-strip__btn svg {
  display: inline-block;
  vertical-align: middle;
  flex-shrink: 0;
}

/* Focus accessible */
.theme-2026 .t26-focusable:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
}
`;

    return <style dangerouslySetInnerHTML={{ __html: css }} />;
}
